import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveLine } from '@nivo/line';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PARAM_DEFS } from 'common/constants/physicalParams';

// Tooltip a nivel de módulo (evita react/no-unstable-nested-components). La unidad ya viene en
// `yFormatted` vía el `yFormat` del chart.
function PointTooltip({ point }) {
  return (
    <Box sx={{ backgroundColor: 'surface.content', border: '1px solid', borderColor: 'gold.main', borderRadius: '8px', px: 1.25, py: 0.75, boxShadow: 4 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 700 }}>{point.data.xFormatted}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, color: 'gold.main', fontVariantNumeric: 'tabular-nums' }}>{point.data.yFormatted}</Typography>
    </Box>
  );
}
PointTooltip.propTypes = { point: PropTypes.object.isRequired };

/**
 * Evolución de un parámetro del progreso físico en el tiempo (reusable cliente/entrenador).
 */
function ProgressChart({ entries }) {
  const theme = useTheme();
  // Solo params con al menos 2 datos (para que tenga sentido la línea).
  const available = useMemo(
    () => PARAM_DEFS.filter((p) => entries.filter((e) => e[p.key] != null).length >= 2),
    [entries]
  );
  const [param, setParam] = useState(available[0]?.key);
  const activeParam = available.find((p) => p.key === param) || available[0];

  const data = useMemo(() => {
    if (!activeParam) return [];
    const points = entries
      .filter((e) => e[activeParam.key] != null)
      .map((e) => ({ x: (e.taken_at || e.created_at || '').slice(0, 10), y: Number(e[activeParam.key]) }))
      .sort((a, b) => (a.x < b.x ? -1 : 1));
    return [{ id: activeParam.label, data: points }];
  }, [entries, activeParam]);

  // Rango Y con padding (20%) para que la línea/área NO toque el eje de fechas y se vea toda la subida.
  const bounds = useMemo(() => {
    const ys = data[0]?.data.map((d) => d.y) ?? [];
    if (ys.length === 0) return { min: 0, max: 1 };
    const lo = Math.min(...ys);
    const hi = Math.max(...ys);
    const pad = lo === hi ? Math.max(Math.abs(lo) * 0.1, 1) : (hi - lo) * 0.2;
    return { min: lo - pad, max: hi + pad };
  }, [data]);

  if (available.length === 0) return null;

  return (
    <Box sx={{ backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: '16px', p: { xs: 2, md: 2.5 }, height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 800 }}>Evolución</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeParam?.key}
          onChange={(_, v) => v && setParam(v)}
          sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.25, py: 0.25, color: 'text.secondary', borderColor: 'hairline.strong' }, '& .Mui-selected': { color: (th) => `${th.palette.secondary.main} !important`, backgroundColor: (th) => `${th.palette.gold.soft} !important` } }}>
          {available.map((p) => <ToggleButton key={p.key} value={p.key}>{p.label}</ToggleButton>)}
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ height: 240 }}>
        <ResponsiveLine
          data={data}
          margin={{ top: 16, right: 20, bottom: 44, left: 44 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: bounds.min, max: bounds.max }}
          curve="monotoneX"
          colors={[theme.palette.gold.main]}
          pointSize={8}
          pointColor={theme.palette.gold.main}
          pointBorderWidth={2}
          pointBorderColor={theme.palette.surface.content}
          enableGridX={false}
          axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: 0 }}
          axisLeft={{ tickSize: 0, tickPadding: 6 }}
          enableArea
          areaOpacity={0.08}
          useMesh
          crosshairType="x"
          yFormat={(value) => `${value}${activeParam?.unit ? ` ${activeParam.unit}` : ''}`}
          tooltip={PointTooltip}
          theme={{ axis: { ticks: { text: { fill: theme.palette.text.secondary, fontSize: 11 } } }, grid: { line: { stroke: theme.palette.hairline.main } } }}
        />
      </Box>
    </Box>
  );
}

ProgressChart.propTypes = { entries: PropTypes.arrayOf(PropTypes.object).isRequired };

export default ProgressChart;
